import { useEffect, useId, useRef, useState, type ReactNode } from "react";

/**
 * Small modal confirmation dialog (same overlay pattern as the Approvals deny dialog).
 * With `requireText`, the user must type that value (case-insensitive, trimmed) before
 * Confirm is enabled — used for destructive actions.
 */
export default function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  requireText,
  requireTextLabel,
  isPending = false,
  error,
  onCancel,
  onConfirm
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  requireText?: string;
  requireTextLabel?: ReactNode;
  isPending?: boolean;
  /** Shown inside the dialog (e.g. the server's `detail`). */
  error?: string | null;
  onCancel: () => void;
  onConfirm: (typedText: string) => void;
}) {
  const titleId = useId();
  const bodyId = useId();
  const inputId = useId();
  const [typed, setTyped] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      setTyped("");
      return;
    }
    // Focus the confirmation input when present, otherwise the (safe) Cancel button.
    const t = window.setTimeout(() => (inputRef.current ?? cancelRef.current)?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape" && !isPending) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel, isPending]);

  if (!open) return null;

  const textMatches =
    requireText === undefined || typed.trim().toLowerCase() === requireText.trim().toLowerCase();
  const canConfirm = textMatches && !isPending;

  return (
    <div
      role="presentation"
      className="directory-dialog-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isPending) onCancel();
      }}
    >
      <div
        role={danger ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        className={`directory-dialog${danger ? " directory-dialog--danger" : ""}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="directory-dialog-title">
          {title}
        </h2>
        <div id={bodyId} className="directory-dialog-body">
          {body}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canConfirm) onConfirm(typed.trim());
          }}
        >
          {requireText !== undefined ? (
            <div className="directory-dialog-field">
              <label htmlFor={inputId}>
                {requireTextLabel ?? (
                  <>
                    Type <strong>{requireText}</strong> to confirm
                  </>
                )}
              </label>
              <input
                ref={inputRef}
                id={inputId}
                type="text"
                className="directory-search directory-dialog-input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                disabled={isPending}
              />
            </div>
          ) : null}

          {error ? (
            <div className="directory-error directory-dialog-error" role="alert">
              {error}
            </div>
          ) : null}

          <div className="directory-dialog-actions">
            <button
              ref={cancelRef}
              type="button"
              className="directory-btn"
              onClick={onCancel}
              disabled={isPending}
            >
              {cancelLabel}
            </button>
            <button
              type="submit"
              className={`directory-btn ${danger ? "directory-btn--danger-solid" : "directory-btn--primary"}`}
              disabled={!canConfirm}
            >
              {isPending ? "Working…" : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
